import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

/**
 * Shape the SSRM grid component exposes onto `gridOptions.context.ssrmGridHost`
 * for this renderer to call into. Kept here (not imported from the grid
 * component file) to break the circular type reference — the grid component
 * imports this renderer for its `gridOptions.components` registration, so we
 * can't import the grid component's type back here.
 */
export interface EditableCellWithUndoGridHost {
  /**
   * True when the given cell currently has a pending in-cell edit
   * tracked in the grid's modifiedCellsByRow Map. Drives whether the
   * undo button renders.
   */
  isCellModified(rowId: string, field: string): boolean;
  /**
   * True when the given cell is currently being persisted by an in-flight
   * save (in the grid's currentSavingCells snapshot). When true the undo
   * button hides — the value is already in flight and `pointer-events:
   * none` is on the cell anyway.
   */
  isCellSaving(rowId: string, field: string): boolean;
  /**
   * Invoked when the user clicks the undo button. Grid implementation
   * reverts node.data via setDataValue (with a sentinel `source`), deletes
   * the entry from modifiedCellsByRow, refreshes the cell, and emits the
   * public (cellUndoClicked) Output for the consumer.
   */
  onCellUndoClicked(rowId: string, field: string): void;
}

/**
 * AG-Grid cell renderer used in place of the default text renderer for any
 * column that the SSRM grid's `wrapColumnDefs` flagged as editable. Renders
 * the cell's display value plus a ghost-style undo overlay button that
 * appears only while the cell has a pending in-cell edit AND is not
 * currently being saved. The button reverts the cell to its original
 * pre-edit value via the host callback.
 *
 * Refresh strategy: `refresh()` returns `true` so AG-Grid keeps the same
 * renderer instance and re-runs `agInit` on cellValueChanged / refreshCells
 * — important so the button's visibility tracks modifiedCellsByRow state
 * changes without flicker.
 */
@Component({
  selector: 'lib-editable-cell-with-undo',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './editable-cell-with-undo.component.html',
  styleUrls: ['./editable-cell-with-undo.component.scss'],
})
export class EditableCellWithUndoComponent implements ICellRendererAngularComp {
  public displayValue = '';
  public isModified = false;
  public isSaving = false;

  private rowId = '';
  private field = '';
  private gridHost?: EditableCellWithUndoGridHost;

  public agInit(params: ICellRendererParams): void {
    this.applyParams(params);
  }

  /**
   * AG-Grid invokes refresh on cell value change / refreshCells. Returning
   * true keeps the same renderer instance (component DOM is reused) and
   * runs our re-evaluation logic; returning false would destroy and
   * recreate the renderer. In-place refresh is cheaper and avoids a flicker
   * on the button visibility transition.
   */
  public refresh(params: ICellRendererParams): boolean {
    this.applyParams(params);
    return true;
  }

  public onUndoClick(event: MouseEvent): void {
    // Prevent the click from bubbling to the cell (which would otherwise
    // start the cell editor under singleClickEdit: true) AND from
    // performing any default browser action.
    event.stopPropagation();
    event.preventDefault();
    if (!this.gridHost) return;
    if (!(this.rowId && this.field)) return;
    this.gridHost.onCellUndoClicked(this.rowId, this.field);
  }

  private applyParams(params: ICellRendererParams): void {
    this.displayValue = this.formatDisplayValue(params);

    const data = params.data as { id?: string | number } | undefined;
    // params.colDef is typed as `ColDef | null | undefined` in AG-Grid
    // v28 since this renderer can be invoked for grouping / footer
    // contexts. We only have meaningful state for data cells with a
    // backing field, so guard accordingly.
    const field = params.colDef?.field;
    if (data?.id == null || !field) {
      this.isModified = false;
      this.isSaving = false;
      this.rowId = '';
      this.field = '';
      this.gridHost = undefined;
      return;
    }

    this.rowId = String(data.id);
    this.field = field;
    this.gridHost = (
      params.context as { ssrmGridHost?: EditableCellWithUndoGridHost } | undefined
    )?.ssrmGridHost;

    this.isModified = this.gridHost?.isCellModified(this.rowId, this.field) ?? false;
    this.isSaving = this.gridHost?.isCellSaving(this.rowId, this.field) ?? false;
  }

  /**
   * Mirror AG-Grid's default text rendering: prefer `valueFormatted`
   * (column's valueFormatter output) when present, else the raw `value`,
   * else empty string. Without this we'd render `undefined` / `null`
   * literally for cells that lack a value.
   */
  private formatDisplayValue(params: ICellRendererParams): string {
    if (params.valueFormatted != null) return String(params.valueFormatted);
    if (params.value == null) return '';
    return String(params.value);
  }
}
